import _ from 'lodash'
import { ApplicationBundle } from './app-bundle'

import { Entity } from './entity'
import { IInboxConfiguration } from './inbox'
import { SchemaIds, SchemaUris } from './schema'
import { PlatformType } from './types/storyboard/storyboard-plan'
import { withTimeout } from '../helpers/utils'
import { getDeviceInfoProvider } from './device'
import permissions, { Permissions } from './types/permissions'

const SYSTEM_FIRM_ID = '00000000-0000-0000-0000-000000000000'
const TMS_BROKER_BUNDLE_ID = '11111111-0000-0000-0000-200000000002'
const TMS_CARRIER_BUNDLE_ID = 'b92c8240-0dab-489e-b219-45c689f82bdf'


export abstract class AbstractSettings {

  protected apis: any

  protected applicationBundle: ApplicationBundle
  protected firm: any
  protected settings: any
  protected mappings: any
  protected portals: any
  protected store: any
  protected user: any
  protected inboxConfiguration: IInboxConfiguration
  protected platform: PlatformType
  public sessionReplayClient: any // log rocket

  constructor(apis) {
    this.apis = apis
    this.store = apis.getStore()
    this.applicationBundle = new ApplicationBundle(apis)
  }

  public abstract initialize()

  // Super Admin
  public get isAdmin(): boolean {
    return this.firm && this.firm.uniqueId === SYSTEM_FIRM_ID
  }

  public get isFirmAdmin(): boolean {
    const activeEdges = this.user?.activeMixins
    return _.some(activeEdges, (mixin) => mixin.entityId === SchemaIds.FIRM_ADMIN)
  }

  public get isManager(): boolean {
    const activeMixins = this.user?.activeMixins
    return _.some(activeMixins, (mixin) => mixin.entityId === SchemaIds.MANAGER)
  }

  public get isMember(): boolean {
    const activeMixins = this.user?.activeMixins
    return _.some(activeMixins, (mixin) => mixin.entityId === SchemaIds.MEMBER)
  }

  public get isRenditionBillingEnabled(): boolean {
    const generalSettings = this.getSettingsByEntityId(SchemaIds.GENERAL_SETTINGS)
    if (!generalSettings) {
      return false
    }
    return _.get(generalSettings, 'generalSettings.isRenditionBillingEnabled', false)
  }

  public get isRenditionBillingV2Enabled(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.get(generalSettings, 'generalSettings.isRenditionBillingV2Enabled', false)
  }

  public get isDriverAppEnabled(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.get(generalSettings, 'generalSettings.isDriverAppEnabled', false)
  }

  public get isYmsEnabled(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.get(generalSettings, 'generalSettings.isYmsEnabled', false)
  }

  public get isAiBlocksEnabled(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.get(generalSettings, 'generalSettings.isAiBlocksEnabled', false)
  }

  public get hasCustomFTPTrigger(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.get(generalSettings, 'generalSettings.hasCustomFTPTrigger', false)
  }

  public get hasCustomSAMLSettings(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.get(generalSettings, 'generalSettings.hasCustomSAMLSettings', false)
  }

  public get hasDriverAppAccess(): boolean {
    const activeMixins = this.user?.activeMixins
    return _.some(activeMixins, (mixin) => mixin.entityId === SchemaIds.DRIVER_DISPATCH_APP)
  }

  public get hasReceivedPortals(): boolean {
    return _.some(this.portals, (portal) => portal.core_portal.to.entityId === this.firm.uniqueId)
  }

  public get isElementsAccessControlEnabled(): boolean {
    const generalSettings = this.getGeneralSettings()
    return _.has(generalSettings, 'generalSettings.elementsAccessControl.apiKey')
  }

  public get logRocketAppId(): string {
    return 'ac9kby/convoy'
  }

  public getApplicationBundle() {
    return this.applicationBundle
  }

  public getBundleSchemas() {
    return this.applicationBundle.getResolvedSchemas()
  }

  public getFirm() {
    return this.firm
  }

  public getUser() {
    return this.user
  }

  public getUserInitial() {
    const firstName = this.user.person?.firstName || ''
    const lastName = this.user.person?.lastName || ''

    const initial =  `${firstName.charAt(0)}${lastName.charAt(0)}`
    return initial.toUpperCase()
  }

  public getMappings() {
    return this.mappings
  }

  public getInitialRoute() {
    const { initialRoute } = this.applicationBundle.getResolvedBundle().applicationBundle
    return initialRoute
  }

  public isDocumentImagingEnabled(): boolean {
    return !_.isNil(this.getDocumentImagingSettings())
  }

  public isBrokerTMSApplication(): boolean {
    const extensionEdges = _.get(this.applicationBundle.getResolvedBundle(), 'applicationBundle.extends', [])
    return _.some(extensionEdges, (edge) => edge.entityId === TMS_BROKER_BUNDLE_ID)
  }

  // TODO(peter/louis): We need to clean this up.
  public isCarrierTMSAppliction(): boolean {
    const extensionEdges = _.get(this.applicationBundle.getResolvedBundle(), 'applicationBundle.extends', [])
    return _.some(extensionEdges, (edge) => edge.entityId === TMS_CARRIER_BUNDLE_ID)
  }

  public get isDATEnabled(): boolean {
    const datSettings = this.getDATSettings()
    return (datSettings !== null)
  }

  public getDATSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.DAT_SETTINGS)
  }

  public getUserDATSettings(): Entity {
    return _.get(this.user, 'dat')
  }

  public getMobileSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.MOBILE_SETTINGS)
  }

  public getKioskSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.KIOSK_SETTINGS)
  }

  public getEntityTypeFilterSettings(): Entity[] {
    return _.filter(this.settings, (settings: Entity) => {
      return settings.hasMixin(SchemaIds.ENTITY_TYPE_SELECTION_FILTER)
    })
  }

  public getInvoiceSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.ACCOUNTS_RECEIVABLE_INVOICE_SETTINGS)
  }

  public getDocumentImagingSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.DOCUMENT_IMAGING_SETTINGS)
  }

  public getGeneralSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.GENERAL_SETTINGS)
  }

  public getQuickBooksOnlineSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.QUICK_BOOKS_ONLINE_SETTINGS)
  }

  public getTMSSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.TMS_SETTINGS)
  }

  public getFtpSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.FTP_SETTINGS)
  }

  public removeFtpSettings() {
    this.removeSettingsByEntityId(SchemaIds.FTP_SETTINGS)
  }

  public getSamlSettings(): Entity {
    return this.getSettingsByEntityId(SchemaIds.SAML_SETTINGS)
  }

  public removeSamlSettings() {
    this.removeSettingsByEntityId(SchemaIds.SAML_SETTINGS)
  }

  public getSettingsByEntityId(entityId): Entity {
    return _.find(this.settings, (settings: any) => {
      const mixins = settings.activeMixins
      return _.some(mixins, (mixin) => mixin.entityId === entityId)
    })
  }

  public removeSettingsByEntityId(entityId): void {
    _.remove(this.settings, (settings: any) => {
      const mixins = settings.activeMixins
      return _.some(mixins, (mixin) => mixin.entityId === entityId)
    })
  }

  public getSettingsByNamespace(namespace): Entity {
    return _.find(this.settings, (settings) => settings[namespace])
  }

  public getSettingsProperty(namespace, property, defaultValue?) {
    const settings = this.getSettingsByNamespace(namespace)
    return _.get(settings, `${namespace}.${property}`, defaultValue)
  }

  public get resumeStoryboardExecutionOnAppActive() {
    return _.get(this.getMobileSettings(), 'mobileSettings.bootstrapConfig.resumeStoryboardExecutionOnAppActive', false)
  }

  public getOrderEntityType(): string {
    if (this.isRenditionBillingV2Enabled) {
      return SchemaUris.RBV2_ORDER
    } else if (!_.isNil(this.getTMSSettings())) {
      return SchemaUris.RBV1_ORDER
    }
  }

  public getOrderIdentifierPath(): string {
    if (this.isRenditionBillingV2Enabled) {
      return 'core_fulfilment_salesOrder.identifier'
    } else if (!_.isNil(this.getTMSSettings())) {
      return 'dispatchOrder.identifier'
    }
  }

  public getInboxConfiguration(): IInboxConfiguration {
    return this.inboxConfiguration
  }

  public getPlatform(): PlatformType {
    return this.platform
  }

  public setPlatform(platform: PlatformType) {
    this.platform = platform
  }

  public get permissions() {
    return permissions
  }

  /*
   * Returns a user identity object for logging/analytics (LogRocket, etc.).
   * All values are returned as strings.
   */
  public buildUserIdentity = () => {
    const { user, firm, platform } = this
    const deviceInfo = getDeviceInfoProvider().getDeviceInfo('')

    const driverUniqueId = _.join(
      _.map(_.get(user, 'user.additionalIdentities'), 'driverUniqueId'),
      ','
    )
    const employeeId = _.get(user, 'user.employeeId', '')
    const roles = _.filter(_.map(_.get(user, 'mixins.active'), 'displayName'), (name) => {
      return name.toLowerCase() !== 'entity'
    })
    const userRoles = _.join(roles, ',')

    return {
      userId: user?.uniqueId ?? '',
      // don't user displayName since it requires some schema loading/validation
      name: _.join([user?.person?.firstName, user?.person?.lastName], ' '),
      firstName: user?.person?.firstName ?? '',
      lastName: user?.person?.lastName ?? '',
      email: user?.person?.emails?.[0]?.value ?? '',
      phone: user?.person?.phoneNumbers?.[0]?.value?.phone ?? '',
      driverUniqueId,
      employeeId,
      userRoles,
      host: this.apis.getAPIHost(),
      platform: platform ?? '',
      firmId: firm?.uniqueId ?? '',
      firmName: firm?.business?.legalName ?? '',

      release: deviceInfo.appVersion ?? '', // same name convention as LogRocket
      deviceName: deviceInfo.name ?? '',
      locale: deviceInfo.locale ?? '',
      timeZoneId: deviceInfo.timezoneId ?? ''
    }
  }

  public getReplaySessionInfo = async (): Promise<{ sessionURL: string | null } | null> => {
    const sessionURL = this.sessionReplayClient?.sessionURL

    if (!_.isEmpty(sessionURL)) {
      // valid url session
      if (sessionURL.startsWith('https')) {
        console.log('replay session url', sessionURL)
        return Promise.resolve({ sessionURL })
      }

      // if it is not empty and not a valid https, then it is an error, like quota exceed, so just return
      console.log('Invalid replay session url:', sessionURL)
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      if (typeof this.sessionReplayClient?.getSessionURL !== 'function') {
        resolve(null)
        return
      }

      this.sessionReplayClient.getSessionURL((sessionURL: string | null) => {
        console.log('replay session url callback', sessionURL)
        resolve({ sessionURL })
      })
    })
  }

  public getReplaySessionInfoWithTimeout = (
    timeoutMs: number
  ): Promise<{ sessionURL: string | null } | null> => {
    return withTimeout(this.getReplaySessionInfo(), timeoutMs).catch((error) => {
      console.warn(`Failed to get replay session URL: ${error.message}`)
      return null
    })
  }

}

